/**
 * Sysadmin Runtime → MCP Sandboxes (Phase 5 PR-D v1) — HIGH-RISK.
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.6 + §10.7 (don't bury kill switches) +
 * Phase 5 architect §2.4 PR-D + §5.4 (Team 4 audit focus):
 *   - Live list of running Tier-2/3 MCP sandbox containers across every
 *     tenant
 *   - Kill action uses a custom Dialog (NOT PR-0's TypedConfirmDialog)
 *     because the ITAR ack checkbox is a third orthogonal precondition
 *     the shared primitive doesn't model. The operator must type the
 *     container_id (falls back to tenant_slug:server_slug when
 *     container_id is null). See
 *     `__tests__/-runtime-mcp-kill-gate.test.ts` for the gate-logic
 *     invariants.
 *   - Reason textarea (≥10 chars) populates the audit row
 *   - ITAR-scoped tenants: a red banner + cross-review checkbox is
 *     required before the kill button enables. The server's
 *     ITAR_CROSS_REVIEW_REQUIRED gate is the second line of defence.
 *
 * Refresh: 10s default via createSysadminQuery — operators watching for
 * a runaway sandbox want fresh state but not log-spam refreshes.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Network,
  Search,
  RefreshCw,
  Skull,
  AlertTriangle,
  ShieldAlert,
  Filter,
  Inbox,
  Clock,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusPill } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { PageError } from '@/components/ui/page-error';
import { Toolbar } from '@/components/ui/toolbar';
import {
  useSysadminMcpSandboxes,
  useKillMcpSandbox,
  type SandboxRow,
  type SandboxStatus,
} from '@/lib/use-sysadmin-runtime-ops';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/runtime/mcp')({
  component: McpSandboxesPage,
});

const STATUS_OPTIONS: SandboxStatus[] = [
  'starting',
  'running',
  'idle',
  'crashed',
  'killed',
  'stopped',
];

function McpSandboxesPage() {
  const listQ = useSysadminMcpSandboxes();
  const [tenantFilter, setTenantFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | SandboxStatus>('all');
  const [killTarget, setKillTarget] = useState<SandboxRow | null>(null);

  const items = listQ.data?.items ?? [];
  const filtered = useMemo(() => {
    return items.filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (tenantFilter) {
        const lc = tenantFilter.toLowerCase();
        const slug = (row.tenantSlug ?? '').toLowerCase();
        const name = (row.tenantName ?? '').toLowerCase();
        if (!slug.includes(lc) && !name.includes(lc)) return false;
      }
      return true;
    });
  }, [items, statusFilter, tenantFilter]);

  const counts = useMemo(() => {
    const c = {
      total: items.length,
      running: 0,
      crashed: 0,
      breakerOpen: 0,
      itar: 0,
    };
    for (const r of items) {
      if (r.status === 'running' || r.status === 'idle') c.running += 1;
      if (r.status === 'crashed') c.crashed += 1;
      if (r.circuitBreakerState !== 'closed') c.breakerOpen += 1;
      if (r.itarTenant) c.itar += 1;
    }
    return c;
  }, [items]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      {/* ── Heading ── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Network className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              MCP Sandboxes
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.6</Badge>
              <Badge variant="secondary" className="text-xs">PR-D</Badge>
              <Badge variant="outline" className="text-xs text-destructive border-destructive/40">
                Kill switch
              </Badge>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => listQ.refetch()}
          disabled={listQ.isFetching}
        >
          <RefreshCw
            className={cn(
              'h-3.5 w-3.5 mr-1.5',
              listQ.isFetching && 'animate-spin',
            )}
          />
          Refresh
        </Button>
      </div>

      {/* ── Summary strip ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Total active"
          value={counts.total}
          icon={Network}
          tone="default"
        />
        <StatTile
          label="Running"
          value={counts.running}
          icon={Network}
          tone={counts.running > 0 ? 'success' : 'muted'}
        />
        <StatTile
          label="Crashed"
          value={counts.crashed}
          icon={AlertTriangle}
          tone={counts.crashed > 0 ? 'destructive' : 'success'}
        />
        <StatTile
          label="Breaker open"
          value={counts.breakerOpen}
          icon={AlertTriangle}
          tone={counts.breakerOpen > 0 ? 'warning' : 'success'}
          hint={counts.itar > 0 ? `${counts.itar} ITAR-scoped` : undefined}
        />
      </div>

      {/* ── Filters ── */}
      <Toolbar>
        <div className="relative flex-1 max-w-sm">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            placeholder="Filter by tenant slug or name"
            className="h-9 text-sm pl-8"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as 'all' | SandboxStatus)}
        >
          <SelectTrigger className="h-9 text-sm w-40">
            <Filter className="h-3.5 w-3.5 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {listQ.dataUpdatedAt > 0 && (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1 ml-auto">
            <Clock className="h-3 w-3" />
            Updated {new Date(listQ.dataUpdatedAt).toLocaleTimeString()}
          </span>
        )}
      </Toolbar>

      {/* ── Body ── */}
      {listQ.error ? (
        <PageError
          title="Failed to load MCP sandboxes"
          message={listQ.error.message}
          onRetry={() => listQ.refetch()}
          retrying={listQ.isFetching}
        />
      ) : listQ.isLoading && filtered.length === 0 ? (
        <div className="py-6 text-center text-muted-foreground text-sm animate-pulse">
          Loading sandbox roster…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title={
            items.length === 0
              ? 'No active sandboxes'
              : 'No sandboxes match the current filter'
          }
          body={
            items.length === 0
              ? 'No Tier-2/3 MCP servers are currently running. Sandbox containers appear here once an install starts a process.'
              : 'Adjust the filter to see other sandboxes.'
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant</TableHead>
              <TableHead>Server</TableHead>
              <TableHead>Container</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Uptime</TableHead>
              <TableHead>Breaker</TableHead>
              <TableHead className="text-right">Egress 24h</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((row) => (
              <SandboxTableRow
                key={row.id}
                row={row}
                onKill={() => setKillTarget(row)}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {/* ── Kill dialog ── */}
      {killTarget && (
        <KillSandboxDialog
          target={killTarget}
          onClose={() => setKillTarget(null)}
        />
      )}
    </div>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────

function SandboxTableRow({
  row,
  onKill,
}: {
  row: SandboxRow;
  onKill: () => void;
}) {
  return (
    <TableRow className={row.itarTenant ? 'bg-destructive/[0.03]' : undefined}>
      <TableCell>
        <div className="flex flex-col">
          <div className="text-xs font-mono">
            {row.tenantSlug ?? <span className="text-muted-foreground">(deleted)</span>}
          </div>
          <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">
            {row.tenantName ?? row.tenantId.slice(0, 8) + '…'}
          </div>
          {row.itarTenant && (
            <Badge
              variant="outline"
              className="text-[9px] mt-1 w-fit border-destructive/40 text-destructive"
            >
              <ShieldAlert className="h-2.5 w-2.5 mr-0.5" />
              ITAR
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-xs font-mono">
          {row.serverSlug ?? <span className="text-muted-foreground">(missing)</span>}
        </div>
        {row.serverDisplayName && (
          <div className="text-[10px] text-muted-foreground">{row.serverDisplayName}</div>
        )}
      </TableCell>
      <TableCell>
        <code className="text-[11px] text-muted-foreground">
          {row.containerId ? row.containerId.slice(0, 12) : '—'}
        </code>
      </TableCell>
      <TableCell>
        <SandboxStatusPill status={row.status} />
      </TableCell>
      <TableCell className="text-xs font-mono tabular-nums">
        {formatUptime(row.uptimeSeconds)}
      </TableCell>
      <TableCell>
        <BreakerPill state={row.circuitBreakerState} />
      </TableCell>
      <TableCell className="text-right text-xs font-mono tabular-nums">
        {row.egressAttempts24h.toLocaleString()}
      </TableCell>
      <TableCell className="text-right">
        {row.status === 'killed' || row.status === 'stopped' ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={onKill}
            title="Kill this MCP sandbox container"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Skull className="h-3.5 w-3.5 mr-1" />
            Kill
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function SandboxStatusPill({ status }: { status: SandboxStatus }) {
  switch (status) {
    case 'running':
      return <StatusPill status="healthy" compact>{status}</StatusPill>;
    case 'idle':
      return <StatusPill status="muted" compact>{status}</StatusPill>;
    case 'starting':
      return <StatusPill status="info" compact>{status}</StatusPill>;
    case 'crashed':
      return <StatusPill status="destructive" compact icon={AlertTriangle}>{status}</StatusPill>;
    case 'killed':
      return <StatusPill status="destructive" compact icon={Skull}>{status}</StatusPill>;
    case 'stopped':
    default:
      return <StatusPill status="muted" compact>{status}</StatusPill>;
  }
}

function BreakerPill({ state }: { state: SandboxRow['circuitBreakerState'] }) {
  switch (state) {
    case 'closed':
      return <StatusPill status="healthy" compact>closed</StatusPill>;
    case 'half_open':
      return <StatusPill status="warning" compact>half-open</StatusPill>;
    case 'open':
      return <StatusPill status="destructive" compact>open</StatusPill>;
    default:
      return <StatusPill status="muted" compact>{state}</StatusPill>;
  }
}

// ─── Kill dialog — typed confirm + ITAR gate ─────────────────────────────

function KillSandboxDialog({
  target,
  onClose,
}: {
  target: SandboxRow;
  onClose: () => void;
}) {
  const kill = useKillMcpSandbox();
  // Confirm phrase: prefer the container id (operators see it in the
  // row); fall back to "tenant_slug:server_slug" when container_id is
  // not known yet (start-up failure).
  const phrase = target.containerId
    ? target.containerId.slice(0, 12)
    : `${target.tenantSlug ?? target.tenantId.slice(0, 8)}:${target.serverSlug ?? 'unknown'}`;

  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [ackItar, setAckItar] = useState(false);

  const phraseMatches = typed.trim() === phrase;
  const reasonValid = reason.trim().length >= 10;
  const itarOk = !target.itarTenant || ackItar;
  const canConfirm = phraseMatches && reasonValid && itarOk;

  const handleConfirm = () => {
    if (!canConfirm) return;
    kill.mutate(
      {
        id: target.id,
        reason: reason.trim(),
        confirmed: true,
        ackItarReview: target.itarTenant ? ackItar : false,
      },
      {
        onSuccess: () => onClose(),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Skull className="h-5 w-5 text-destructive" />
            Kill MCP sandbox
          </DialogTitle>
          <DialogDescription>
            Killing a sandbox terminates the container immediately and
            may interrupt in-flight tenant tool calls. This action is
            irreversible — the install must spawn a fresh container.
          </DialogDescription>
        </DialogHeader>

        {/* ITAR red banner */}
        {target.itarTenant && (
          <div className="rounded border border-destructive/40 bg-destructive/10 p-3 text-xs space-y-2">
            <div className="flex items-center gap-2 font-semibold text-destructive">
              <ShieldAlert className="h-4 w-4" />
              ITAR-scoped tenant
            </div>
            <p>
              The owning tenant has ITAR enabled. Killing this sandbox
              may sever evidence chain-of-custody for an in-progress
              regulated workflow. Complete the cross-review with the
              compliance officer before proceeding. The server requires
              your acknowledgement below in addition to the typed
              container id.
            </p>
          </div>
        )}

        {/* Target summary */}
        <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Tenant</span>
            <span className="font-mono">
              {target.tenantSlug ?? target.tenantId}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Server</span>
            <span className="font-mono">{target.serverSlug ?? '—'}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Container</span>
            <span className="font-mono">{target.containerId ?? '—'}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Status</span>
            <SandboxStatusPill status={target.status} />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Breaker</span>
            <BreakerPill state={target.circuitBreakerState} />
          </div>
        </div>

        {/* Reason */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">
            Reason <span className="text-muted-foreground">(audit trail, ≥10 chars)</span>
          </Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Sandbox stuck in restart loop after upstream API auth change"
            className="min-h-[60px] text-sm"
          />
          {!reasonValid && reason.length > 0 && (
            <p className="text-[10px] text-destructive">
              Reason must be at least 10 characters.
            </p>
          )}
        </div>

        {/* Typed confirm */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">
            Type{' '}
            <code className="px-1 py-0.5 rounded bg-muted text-foreground">
              {phrase}
            </code>{' '}
            to confirm
          </Label>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={phrase}
            className="h-9 text-sm font-mono"
            autoFocus
          />
          {typed.length > 0 && !phraseMatches && (
            <p className="text-[10px] text-destructive">
              Phrase must match exactly.
            </p>
          )}
        </div>

        {/* ITAR ack */}
        {target.itarTenant && (
          <label className="flex items-start gap-2 cursor-pointer text-xs">
            <Checkbox
              checked={ackItar}
              onCheckedChange={(v) => setAckItar(v === true)}
              className="mt-0.5"
            />
            <span>
              I acknowledge the ITAR cross-review requirement and accept
              responsibility for any evidence chain-of-custody impact.
            </span>
          </label>
        )}

        {kill.error && (
          <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
            {kill.error.message}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleConfirm}
            disabled={!canConfirm || kill.isPending}
          >
            <Skull className="h-3.5 w-3.5 mr-1" />
            {kill.isPending ? 'Killing…' : 'Kill sandbox'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const mins = Math.floor((seconds % 3_600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m`;
  return `${seconds}s`;
}
